import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { ReasonDialog } from '../components/ReasonDialog';
import { StatusBadge } from '../components/StatusBadge';
import { saveDownloadedExport } from '../downloadFile';
import { exportErrorMessage, isForbidden } from '../exportErrors';
import { canBeReplaced, canBeReversed, formatDate, formatDateTime, periodLabel, shortChecksum } from '../exportFormat';
import { LEGACY_EXPLANATION, LEGACY_LABEL, SOURCE_LABEL, describeNetPay, describeWage, dimensionLabel, isLegacyOptions } from '../exportOptions';
import {
  exportKindLabels, exportModeLabels, exportStatusLabels, type ExportAuditItem, type ExportDetail, type ExportKind, type ExportStatus, type ExportSummary, type Paged,
} from '../exportTypes';
import { accountingGateway } from '../index';
import { formatCents } from '../money';
import { useExportCapabilities } from '../useExportCapabilities';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

type LoadState = { kind: 'loading' } | { kind: 'ready'; data: Paged<ExportSummary> } | { kind: 'forbidden' } | { kind: 'error'; message: string };
type DetailState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; data: ExportDetail } | { kind: 'error'; message: string };
type AuditState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; items: ExportAuditItem[] } | { kind: 'unavailable'; message: string };

const PAGE_SIZE = 25;
const STATUSES: ExportStatus[] = ['Generated', 'Downloaded', 'ReversalRequired', 'Reversed', 'Superseded', 'Failed'];
const KINDS: ExportKind[] = ['Original', 'Reversal', 'Replacement'];

export function ExportHistoryTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { caps } = useExportCapabilities();
  const selectedId = searchParams.get('exportId') ?? '';
  const canReverse = caps?.canReverse !== false;
  const canWrite = caps?.canGenerate !== false;
  const canAudit = caps?.canViewAudit !== false;

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [status, setStatus] = useState<ExportStatus | ''>('');
  const [kind, setKind] = useState<ExportKind | ''>('');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<DetailState>({ kind: 'idle' });
  const [audit, setAudit] = useState<AuditState>({ kind: 'idle' });
  const [dialog, setDialog] = useState<{ kind: 'reversal' | 'replacement'; target: ExportSummary } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const detailHeading = useRef<HTMLHeadingElement>(null);
  const focusDetail = useRef(false);

  const select = useCallback((id: string) => {
    focusDetail.current = true;
    setDetail(id ? { kind: 'loading' } : { kind: 'idle' });
    setAudit(id ? { kind: 'loading' } : { kind: 'idle' });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('exportId', id); else next.delete('exportId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway
      .listExports({ ...(status ? { status } : {}), ...(kind ? { kind } : {}), page, pageSize: PAGE_SIZE }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setState({ kind: 'ready', data }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: exportErrorMessage(err, 'Could not load the export history.') });
      });
    return () => controller.abort();
  }, [status, kind, page, reloadNonce]);

  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController();
    // Reading an export does NOT look at the payroll: only the status check does. So opening an export that could still change asks the server to compare it
    // with the payroll as it stands now (an export that is no longer current becomes Reversal required). People who cannot generate simply see the stored status.
    (async () => {
      const data = await accountingGateway.getExport(selectedId, controller.signal);
      if (!controller.signal.aborted && canWrite && data.kind !== 'Reversal' && (data.status === 'Generated' || data.status === 'Downloaded')) {
        try {
          const synced = await accountingGateway.syncPayrollStatus(selectedId);
          if (synced.status !== data.status && !controller.signal.aborted) setReloadNonce((n) => n + 1); // the list shows the new status too
          return synced;
        } catch {
          return data; // the check is a refinement: if it cannot run, the stored export is still shown
        }
      }
      return data;
    })()
      .then((data) => { if (!controller.signal.aborted) setDetail({ kind: 'ready', data }); })
      .catch((err) => { if (!controller.signal.aborted) setDetail({ kind: 'error', message: exportErrorMessage(err, 'Could not load this export.') }); });
    if (canAudit) {
      accountingGateway.getExportAudit(selectedId, controller.signal)
        .then((items) => { if (!controller.signal.aborted) setAudit({ kind: 'ready', items }); })
        .catch((err) => { if (!controller.signal.aborted) setAudit({ kind: 'unavailable', message: isForbidden(err) ? 'You do not have permission to view the audit history.' : exportErrorMessage(err, 'The audit history could not be loaded.') }); });
    }
    return () => controller.abort();
  }, [selectedId, reloadNonce, canAudit, canWrite]);

  useEffect(() => {
    if (detail.kind === 'ready' && focusDetail.current) {
      focusDetail.current = false;
      detailHeading.current?.focus();
    }
  }, [detail]);

  async function download(e: ExportSummary) {
    setNotice(null);
    try {
      const file = await accountingGateway.downloadExport(e.id);
      saveDownloadedExport(file);
      setNotice(`Downloaded ${file.fileName}.`);
      setReloadNonce((n) => n + 1);
    } catch (err) {
      setNotice(exportErrorMessage(err, 'The file could not be downloaded.'));
    }
  }

  async function syncStatus(e: ExportSummary) {
    setBusy(true);
    setNotice(null);
    try {
      const updated = await accountingGateway.syncPayrollStatus(e.id);
      setNotice(updated.status === 'ReversalRequired'
        ? 'The payroll was corrected after this export, so a reversal is now required.'
        : 'The payroll has not changed since this export. Nothing to do.');
      setReloadNonce((n) => n + 1);
    } catch (err) {
      setNotice(exportErrorMessage(err, 'The payroll status could not be checked.'));
    } finally {
      setBusy(false);
    }
  }

  function done(created: ExportDetail, what: 'reversal' | 'replacement') {
    setDialog(null);
    setNotice(
      what === 'reversal'
        ? `Reversal artifact ${created.journalReference} generated. Nothing was posted to any accounting system: download the file to apply it yourself.`
        : `Replacement export ${created.journalReference} generated from the corrected payroll. Nothing was posted to any accounting system.`,
    );
    select(created.id);
    setReloadNonce((n) => n + 1);
  }

  if ((caps && !caps.canPreview) || state.kind === 'forbidden') {
    return (
      <section className="ax-tab" aria-labelledby="ax-hist-title">
        <h3 id="ax-hist-title">History</h3>
        <AccessDeniedNotice requiredPermissionLabel="permission to view accounting exports" />
      </section>
    );
  }

  const data = state.kind === 'ready' ? state.data : null;
  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;
  const refById = new Map((data?.items ?? []).map((e) => [e.id, e.journalReference]));
  const shown = detail.kind === 'ready' && detail.data.id === selectedId ? detail.data : null;

  return (
    <section className="ax-tab" aria-labelledby="ax-hist-title">
      <h3 id="ax-hist-title">History</h3>
      <p className="muted ax-lead">
        Every export EPN has generated, with its status and checksum. An export is never edited or deleted. If the payroll changes after it was exported, generate a
        reversal artifact (and then a replacement): these are new files for you to apply in your accounting software; nothing is posted for you.
      </p>
      {notice && <p className="jr-notice jr-notice--ok" role="status" data-testid="history-notice">{notice}</p>}

      <div className="ax-toolbar" role="search" aria-label="Filter exports">
        <div className="jr-field">
          <label htmlFor="ax-h-status">Status</label>
          <select id="ax-h-status" value={status} onChange={(e) => { setStatus(e.target.value as ExportStatus | ''); setPage(1); }}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{exportStatusLabels[s]}</option>)}
          </select>
        </div>
        <div className="jr-field">
          <label htmlFor="ax-h-kind">Kind</label>
          <select id="ax-h-kind" value={kind} onChange={(e) => { setKind(e.target.value as ExportKind | ''); setPage(1); }}>
            <option value="">All kinds</option>
            {KINDS.map((k) => <option key={k} value={k}>{exportKindLabels[k]}</option>)}
          </select>
        </div>
      </div>

      {state.kind === 'loading' && <p role="status" className="muted">Loading export history…</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={() => { setState({ kind: 'loading' }); setReloadNonce((n) => n + 1); }} />}

      {data && data.items.length === 0 && (
        <p className="muted" data-testid="no-exports">{status || kind ? 'No exports match these filters.' : 'No exports yet. Generate one from the Payroll Export tab and it will appear here.'}</p>
      )}

      {data && data.items.length > 0 && (
        <div className="jr-table-scroll" role="region" aria-label="Export history table" tabIndex={0}>
          <table className="jr-table jr-table--actions ax-table-wide" data-testid="history-table">
            <caption className="ax-caption">Payroll exports, newest first ({data.totalCount})</caption>
            <thead>
              <tr>
                <th scope="col">Journal reference</th>
                <th scope="col">Kind</th>
                <th scope="col">Mode</th>
                <th scope="col">Options</th>
                <th scope="col">Check date</th>
                <th scope="col" className="num">Total debits</th>
                <th scope="col" className="num">Total credits</th>
                <th scope="col" className="num">Lines</th>
                <th scope="col">Status</th>
                <th scope="col">Checksum</th>
                <th scope="col">Linked export</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} aria-selected={e.id === selectedId ? true : undefined}>
                  <th scope="row">{e.journalReference}</th>
                  <td>{exportKindLabels[e.kind]}</td>
                  <td>{exportModeLabels[e.mode]}</td>
                  <td data-testid={`history-options-${e.id}`}>{isLegacyOptions(e.options) ? <span className="ax-legacy-tag">{LEGACY_LABEL}</span> : <>{describeNetPay(e.options, e.mode)}<span className="ax-date jr-hint">Wages: {describeWage(e.options)}</span></>}</td>
                  <td>{formatDate(e.checkDate)}</td>
                  <td className="num">{formatCents(e.totalDebitsCents)}</td>
                  <td className="num">{formatCents(e.totalCreditsCents)}</td>
                  <td className="num">{e.lineCount}</td>
                  <td><StatusBadge status={e.status} /></td>
                  <td><code title={e.checksum}>{shortChecksum(e.checksum)}</code></td>
                  <td>
                    {e.reversalOfExportId && <button type="button" className="ax-link-btn" onClick={() => select(e.reversalOfExportId!)}>Reverses {refById.get(e.reversalOfExportId) ?? 'an export'}</button>}
                    {e.replacesExportId && <button type="button" className="ax-link-btn" onClick={() => select(e.replacesExportId!)}>Replaces {refById.get(e.replacesExportId) ?? 'an export'}</button>}
                    {e.reversedByExportId && <button type="button" className="ax-link-btn" onClick={() => select(e.reversedByExportId!)}>Reversed by {refById.get(e.reversedByExportId) ?? 'a reversal'}</button>}
                    {e.supersededByExportId && <button type="button" className="ax-link-btn" onClick={() => select(e.supersededByExportId!)}>Replaced by {refById.get(e.supersededByExportId) ?? 'a replacement'}</button>}
                  </td>
                  <td className="ax-row-actions">
                    <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => select(e.id)} aria-label={`Details for ${e.journalReference}`}>Details</button>
                    {e.status !== 'Failed' && (
                      <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => void download(e)} aria-label={`Download ${e.journalReference}`}>Download</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalCount > data.pageSize && (
        <nav className="jr-pager" aria-label="Pagination">
          <span>Page {data.page} of {totalPages}</span>
          <span className="jr-pager__nav">
            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1}>Previous</button>
            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={data.page >= totalPages}>Next</button>
          </span>
        </nav>
      )}

      {selectedId && detail.kind === 'error' && <p className="jr-error jr-error--block" role="alert">{detail.message}</p>}

      {shown && (
        <section className="ax-detail" aria-labelledby="ax-detail-title" data-testid="export-detail">
          <h4 id="ax-detail-title" ref={detailHeading} tabIndex={-1}>Export {shown.journalReference}</h4>
          {shown.status === 'ReversalRequired' && (
            <p className="jr-warning" role="note"><span aria-hidden="true">⚠ </span>The payroll was corrected after this export, so it must be reversed. Generate a reversal artifact below.</p>
          )}
          {shown.status === 'Reversed' && (
            <p className="jr-notice" role="note">This export was reversed. When the corrected payroll is final, generate a replacement export.</p>
          )}
          {shown.status === 'Failed' && (
            <p className="jr-error jr-error--block" role="note">Generating the file failed. No file exists for this export. Support can find it with the reference {shown.operationId}.</p>
          )}
          <dl className="jr-facts">
            <div className="jr-facts__row"><dt>Status</dt><dd><StatusBadge status={shown.status} /></dd></div>
            <div className="jr-facts__row"><dt>Kind</dt><dd>{exportKindLabels[shown.kind]}</dd></div>
            <div className="jr-facts__row"><dt>Mode</dt><dd>{exportModeLabels[shown.mode]}</dd></div>
            <div className="jr-facts__row" data-testid="detail-options"><dt>Options recorded</dt>
              <dd>
                {isLegacyOptions(shown.options)
                  ? <><span className="ax-legacy-tag" data-testid="detail-legacy">{LEGACY_LABEL}</span> {shown.options.recorded ? 'No option was set, so the original rules applied.' : 'Made before export options existed.'}</>
                  : <>Net pay: {describeNetPay(shown.options, shown.mode)}. Wages: {describeWage(shown.options)}. Source: {shown.options.source ? SOURCE_LABEL[shown.options.source] : 'not stated'}.</>}
              </dd>
            </div>
            {shown.options.wageGroupDimension && <div className="jr-facts__row"><dt>Group dimension</dt><dd>{dimensionLabel(shown.options.wageGroupDimension)}</dd></div>}
            {shown.options.settingsVersion !== null && <div className="jr-facts__row"><dt>Company settings version</dt><dd>{shown.options.settingsVersion}</dd></div>}
            {shown.options.groupConfigVersion !== null && <div className="jr-facts__row"><dt>Group configuration version</dt><dd>{shown.options.groupConfigVersion}</dd></div>}
            <div className="jr-facts__row"><dt>Target</dt><dd>CSV file (schema version {shown.csvSchemaVersion})</dd></div>
            <div className="jr-facts__row"><dt>Pay period</dt><dd>{periodLabel(shown.periodStart, shown.periodEnd)}</dd></div>
            <div className="jr-facts__row"><dt>Check date</dt><dd>{formatDate(shown.checkDate)}</dd></div>
            <div className="jr-facts__row"><dt>Accounting date</dt><dd>{formatDate(shown.accountingDate)}</dd></div>
            <div className="jr-facts__row"><dt>Lines</dt><dd>{shown.lineCount}</dd></div>
            <div className="jr-facts__row"><dt>Total debits</dt><dd>{formatCents(shown.totalDebitsCents)}</dd></div>
            <div className="jr-facts__row"><dt>Total credits</dt><dd>{formatCents(shown.totalCreditsCents)}</dd></div>
            <div className="jr-facts__row"><dt>Highest mapping version</dt><dd>{shown.mappingVersionMax}</dd></div>
            <div className="jr-facts__row"><dt>Generated</dt><dd>{formatDateTime(shown.generatedAtUtc)}</dd></div>
            <div className="jr-facts__row"><dt>Downloads</dt><dd>{shown.downloadCount}{shown.firstDownloadedAtUtc ? ` (first ${formatDateTime(shown.firstDownloadedAtUtc)})` : ''}</dd></div>
            {shown.reason && <div className="jr-facts__row"><dt>Reason</dt><dd>{shown.reason}</dd></div>}
            <div className="jr-facts__row ax-checksum"><dt>Checksum (SHA-256 of the file)</dt><dd><code data-testid="detail-checksum">{shown.checksum || 'None'}</code></dd></div>
          </dl>

          <div className="jr-actions">
            {shown.status !== 'Failed' && <button type="button" className="jr-btn jr-btn--secondary" onClick={() => void download(shown)}>Download CSV</button>}
            {canBeReversed(shown) && canWrite && shown.status !== 'ReversalRequired' && (
              <button type="button" className="jr-btn jr-btn--secondary" onClick={() => void syncStatus(shown)} disabled={busy}>Check payroll status</button>
            )}
            {canBeReversed(shown) && canReverse && (
              <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'reversal', target: shown })}>Generate reversal artifact</button>
            )}
            {canBeReplaced(shown) && canReverse && (
              <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'replacement', target: shown })}>Generate replacement</button>
            )}
          </div>
          {canBeReversed(shown) && !canReverse && <p className="jr-hint">Generating a reversal needs a permission you do not have.</p>}

          <h5>State history</h5>
          <div className="jr-table-scroll" role="region" aria-label="State history" tabIndex={0}>
            <table className="jr-table">
              <caption className="ax-caption">Every state this export has been in</caption>
              <thead><tr><th scope="col">When</th><th scope="col">From</th><th scope="col">To</th><th scope="col">Reason</th></tr></thead>
              <tbody>
                {shown.stateHistory.map((h, i) => (
                  <tr key={`${h.occurredAtUtc}-${i}`}>
                    <td>{formatDateTime(h.occurredAtUtc)}</td>
                    <td>{h.fromStatus ? (exportStatusLabels[h.fromStatus] ?? h.fromStatus) : 'New'}</td>
                    <td>{exportStatusLabels[h.toStatus] ?? h.toStatus}</td>
                    <td>{h.reason ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isLegacyOptions(shown.options) && <p className="jr-hint" data-testid="detail-legacy-note">{LEGACY_EXPLANATION}</p>}
          <details className="ax-block">
            <summary>Journal lines ({shown.lines.length})</summary>
            <div className="jr-table-scroll" role="region" aria-label="Stored journal lines" tabIndex={0}>
              <table className="jr-table">
                <caption className="ax-caption">The stored lines of this export (a snapshot; later mapping changes never alter them)</caption>
                <thead><tr><th scope="col">Line</th><th scope="col">GL account</th><th scope="col" className="num">Debit</th><th scope="col" className="num">Credit</th><th scope="col">Description</th><th scope="col" className="num">Mapping version</th>
                  {shown.lines.some((l) => l.groupLabel || l.resolutionReason) && <th scope="col">Group and why this account</th>}</tr></thead>
                <tbody>
                  {shown.lines.map((l) => (
                    <tr key={l.lineNumber}>
                      <td>{l.lineNumber}</td>
                      <td><strong>{l.accountNumber}</strong> {l.accountName}</td>
                      <td className="num">{l.side === 'Debit' ? formatCents(l.amountCents) : ''}</td>
                      <td className="num">{l.side === 'Credit' ? formatCents(l.amountCents) : ''}</td>
                      <td>{l.description}</td>
                      <td className="num">{l.mappingVersion}</td>
                      {shown.lines.some((x) => x.groupLabel || x.resolutionReason) && (
                        <td>{l.groupLabel && <strong>{l.groupLabel}</strong>}{l.groupLabel && l.resolutionReason ? ': ' : ''}{l.resolutionReason ?? ''}{l.employeeReference ? ` (${l.employeeReference})` : ''}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          {canAudit && (
            <section className="ax-block" aria-labelledby="ax-audit-title" data-testid="audit-history">
              <h5 id="ax-audit-title">Audit history</h5>
              {audit.kind === 'loading' && <p role="status" className="muted">Loading audit history…</p>}
              {audit.kind === 'unavailable' && <p className="muted">{audit.message}</p>}
              {audit.kind === 'ready' && audit.items.length === 0 && <p className="muted">No audit events yet.</p>}
              {audit.kind === 'ready' && audit.items.length > 0 && (
                <div className="jr-table-scroll" role="region" aria-label="Audit history table" tabIndex={0}>
                  <table className="jr-table ax-audit">
                    <caption className="ax-caption">Audit events for this export (ids, counts and totals only)</caption>
                    <thead><tr><th scope="col">When</th><th scope="col">Event</th><th scope="col">Who</th><th scope="col">Details</th></tr></thead>
                    <tbody>
                      {audit.items.map((a) => (
                        <tr key={a.id}><td>{formatDateTime(a.occurredAtUtc)}</td><td>{a.eventType}</td><td>{a.actorEmailSnapshot ?? a.actorType ?? ''}</td><td><code>{a.metadataJson ?? ''}</code></td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </section>
      )}

      {dialog && (
        <ReasonDialog kind={dialog.kind} target={dialog.target} onClose={() => setDialog(null)} onDone={(created) => done(created, dialog.kind)} />
      )}
    </section>
  );
}
